import { useState, useEffect } from 'react';
import { User, Phone, MapPin, Briefcase, GraduationCap, Calendar, Clock, CreditCard, CheckCircle, ChevronRight, AlertCircle } from 'lucide-react';
import { FormData } from '../App';
import { interviewService } from '../services/interview.service';

interface Step3Props {
  formData: FormData;
  onBack?: () => void;
  onSubmit: () => void;
  userId: number;
}

export function Step3Confirmation({ formData, onBack, onSubmit: _onSubmit, userId }: Step3Props) {
  const [isBooking, setIsBooking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const getGradeLabel = (grade: string) => {
    return grade === 'pre-school' ? 'کودکستان' : 'کلاس اول';
  };

  const getEducationLabel = (education: string) => {
    const labels: { [key: string]: string } = {
      'high-school': 'زیر دیپلم',
      'diploma': 'دیپلم',
      'associate': 'کاردانی',
      'bachelor': 'کارشناسی',
      'master': 'کارشناسی ارشد',
      'doctorate': 'دکترا',
    };
    return labels[education] || education;
  };

  const handleSubmit = async () => {
    if (!formData.selectedDate || !formData.selectedTime) {
      setError('تاریخ یا زمان مصاحبه انتخاب نشده است');
      return;
    }

    // Final confirmation
    const confirmed = window.confirm(
      `آیا از ثبت نهایی پیش‌ثبت‌نام اطمینان دارید؟\n\nپس از تأیید، به درگاه پرداخت منتقل خواهید شد.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setIsBooking(true);
      setError(null);

      const response = await interviewService.bookSlot(
        formData.selectedDate,
        formData.selectedTime,
        userId
      );

      if (response.success && response.payment_url) {
        // Redirect to payment gateway
        window.location.href = response.payment_url;
      } else {
        throw new Error('خطا در دریافت لینک پرداخت');
      }
    } catch (err: any) {
      console.error('Error booking slot:', err);
      setError(err.message || 'خطا در رزرو زمان مصاحبه. لطفاً دوباره تلاش کنید.');
      setIsBooking(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      <div
        className="rounded-2xl p-6 border-2 flex items-center gap-4"
        style={{
          backgroundColor: '#F0F9F8',
          borderColor: 'var(--secondary-green)',
        }}
      >
        <CheckCircle className="w-8 h-8 flex-shrink-0" style={{ color: 'var(--secondary-green)' }} />
        <div>
          <h3 className="text-xl mb-1" style={{ color: 'var(--secondary-green)' }}>
            بررسی اطلاعات شما
          </h3>
          <p style={{ color: 'var(--text-gray)' }}>
            لطفاً تمام جزئیات زیر را قبل از ثبت نهایی پیش‌ثبت‌نام بررسی کنید.
          </p>
        </div>
      </div>

      {/* Student Information Card */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2" style={{ borderColor: 'var(--border-light)' }}>
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#F0F9F8' }}>
            <User className="w-6 h-6" style={{ color: 'var(--secondary-green)' }} />
          </div>
          <h2 className="text-2xl" style={{ color: 'var(--secondary-green)' }}>
            اطلاعات دانش‌آموز
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <InfoItem icon={User} label="نام و نام خانوادگی" value={`${formData.firstName} ${formData.lastName}`} />
          <InfoItem icon={GraduationCap} label="پایه تحصیلی مورد نظر" value={getGradeLabel(formData.desiredGrade)} />
          <InfoItem icon={CreditCard} label="کد ملی" value={formData.nationalId} />
          <InfoItem icon={Calendar} label="تاریخ تولد" value={formData.dateOfBirth} />
          <InfoItem icon={MapPin} label="محل تولد" value={formData.placeOfBirth} />
          <div className="sm:col-span-2">
            <InfoItem icon={MapPin} label="آدرس منزل" value={formData.homeAddress} />
          </div>
        </div>
      </div>

      {/* Parent Information Card */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2" style={{ borderColor: 'var(--border-light)' }}>
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEF9EC' }}>
            <User className="w-6 h-6" style={{ color: 'var(--primary-gold)' }} />
          </div>
          <h2 className="text-2xl" style={{ color: 'var(--secondary-green)' }}>
            اطلاعات والدین
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <InfoItem icon={User} label="نام پدر" value={formData.fatherName} />
          <InfoItem icon={Phone} label="شماره تماس پدر" value={formData.fatherPhone} />
          <InfoItem icon={Phone} label="شماره تماس مادر" value={formData.motherPhone} />
          <InfoItem icon={Briefcase} label="شغل پدر" value={formData.fatherJob} />
          <InfoItem icon={GraduationCap} label="تحصیلات پدر" value={getEducationLabel(formData.fatherEducation)} />
          <InfoItem icon={GraduationCap} label="تحصیلات مادر" value={getEducationLabel(formData.motherEducation)} />
        </div>
      </div>

      {/* Appointment Details Card */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2" style={{ borderColor: 'var(--border-light)' }}>
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#FEF9EC' }}>
            <Calendar className="w-6 h-6" style={{ color: 'var(--primary-gold)' }} />
          </div>
          <h2 className="text-2xl" style={{ color: 'var(--secondary-green)' }}>
            قرار مصاحبه
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <InfoItem icon={Calendar} label="تاریخ مصاحبه" value={formData.selectedDate} />
          <InfoItem icon={Clock} label="ساعت مصاحبه" value={formData.selectedTime} />
        </div>

        {/* Payment Summary */}
        <div 
          className="mt-6 p-4 rounded-xl flex items-center justify-between"
          style={{ backgroundColor: '#FEF9EC' }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-white">
              <CreditCard className="w-5 h-5" style={{ color: 'var(--secondary-green)' }} />
            </div>
            <div>
              <div className="text-sm" style={{ color: 'var(--text-gray)' }}>مبلغ پرداختی</div>
              <div className="text-xl" style={{ color: 'var(--secondary-green)' }}>
                ۱۵۰,۰۰۰ تومان
              </div>
            </div>
          </div>
          <div 
            className="px-4 py-2 rounded-lg text-sm"
            style={{ 
              backgroundColor: 'var(--secondary-green)',
              color: 'white'
            }}
          >
            قابل بازگشت
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={`flex flex-col sm:flex-row gap-4 ${onBack ? 'justify-between' : 'justify-start'}`}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={isBooking}
            className="px-6 py-3 rounded-xl border-2 transition-all hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
            style={{
              borderColor: 'var(--border-light)',
              color: 'var(--text-gray)',
            }}
          >
            <ChevronRight className="w-5 h-5" />
            ویرایش اطلاعات
          </button>
        )}

        <button
          onClick={handleSubmit}
          disabled={isBooking}
          className="px-8 py-4 rounded-xl text-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: 'var(--secondary-green)',
            color: 'white',
          }}
        >
          {isBooking ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
              در حال انتقال به درگاه پرداخت...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6" />
              تایید نهایی و پرداخت
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

interface InfoItemProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

function InfoItem({ icon: Icon, label, value }: InfoItemProps) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 mt-1">
        <Icon className="w-5 h-5" style={{ color: 'var(--text-gray)' }} />
      </div>
      <div className="flex-1">
        <div className="text-sm mb-1" style={{ color: 'var(--text-gray)' }}>
          {label}
        </div>
        <div className="text-base" style={{ color: 'var(--text-dark)' }}>
          {value || '—'}
        </div>
      </div>
    </div>
  );
}